import { CONFIG } from "../config";
import type { Chord, NoteEvent } from "../types";

/**
 * Group notes into chords by score onset: notes whose
 * onsets fall within the configurable window of the group's anchor are gated
 * together in wait-mode. This is window-free input timing's counterpart — it
 * shapes the EXPECTED set, not how presses are matched.
 *
 * Input must be the hand-filtered note list; the result is ascending by onset.
 */
export function groupChords(notes: NoteEvent[]): Chord[] {
  if (notes.length === 0) return [];

  const sorted = [...notes].sort((a, b) => a.onset - b.onset);
  const windowSeconds = CONFIG.chordWindowMs / 1000;
  const chords: Chord[] = [];

  const first = sorted[0];
  if (!first) return [];

  let bucket: NoteEvent[] = [];
  let anchor = first.onset;

  for (const note of sorted) {
    if (bucket.length > 0 && note.onset - anchor > windowSeconds) {
      chords.push(makeChord(bucket));
      bucket = [];
      anchor = note.onset;
    }
    bucket.push(note);
  }
  if (bucket.length > 0) chords.push(makeChord(bucket));

  return chords;
}

function makeChord(notes: NoteEvent[]): Chord {
  let onset = Number.POSITIVE_INFINITY;
  for (const n of notes) onset = Math.min(onset, n.onset);
  const pitches = [...new Set(notes.map((n) => n.midi))].sort((a, b) => a - b);
  return { onset, pitches, notes };
}
